import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  FiFileText,
  FiFolder,
  FiFolderPlus,
  FiFilePlus,
  FiSave,
  FiTrash2,
  FiCpu,
  FiChevronDown,
  FiChevronRight,
  FiX,
} from "react-icons/fi";

import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";
import { getSelfPage } from "../data";

export default function Files() {
  const { organization } = useStore();
  const navigate = useNavigate();

  const [items, setItems] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState({ title: "", tags: [], content: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!organization?._id) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?._id]);

  async function load() {
    try {
      setItems(null);
      const res = await api.post("/brain/pages/search", {
        organization_id: organization._id,
        organization_name: organization.name,
      });
      if (!res.ok) throw new Error("load failed");
      setItems(res.data);
    } catch (e) {
      toast.error("Failed to load brain");
      setItems([]);
    }
  }

  const activeItem = useMemo(() => items?.find((i) => i._id === activeId) || null, [items, activeId]);

  function confirmDiscard() {
    if (!dirty) return true;
    return window.confirm("Discard unsaved changes?");
  }

  function selectItem(item) {
    if (!confirmDiscard()) return;
    setActiveId(item._id);
    setDraft({
      title: item.title || "",
      tags: Array.isArray(item.tags) ? item.tags : [],
      content: item.content || "",
    });
    setDirty(false);
  }

  function updateDraft(patch) {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (!activeItem) return;
    const same =
      next.title === (activeItem.title || "") &&
      next.content === (activeItem.content || "") &&
      arraysEq(next.tags, activeItem.tags || []);
    setDirty(!same);
  }

  async function save() {
    if (!activeItem) return;
    try {
      setSaving(true);
      const res = await api.put(`/brain/pages/${activeItem._id}`, {
        organization_id: organization._id,
        title: draft.title,
        content: draft.content,
        tags: draft.tags,
      });
      if (!res.ok) throw new Error("save failed");
      setItems((prev) => prev.map((i) => (i._id === activeItem._id ? res.data : i)));
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!activeItem || activeItem.is_system) return;
    const label = activeItem.type === "folder" ? "folder and everything inside it" : "page";
    if (!window.confirm(`Delete this ${label}? This can't be undone.`)) return;
    try {
      const res = await api.remove(`/brain/pages/${activeItem._id}`, {
        organization_id: organization._id,
      });
      if (!res.ok) throw new Error("delete failed");
      setItems((prev) => prev.filter((i) => i._id !== activeItem._id));
      setActiveId(null);
      setDirty(false);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  }

  async function create(type, parent_id) {
    if (!confirmDiscard()) return;
    try {
      const res = await api.post("/brain/pages", {
        organization_id: organization._id,
        organization_name: organization.name,
        type,
        parent_id: parent_id || null,
        title: type === "folder" ? "New folder" : "Untitled",
      });
      if (!res.ok) throw new Error("create failed");
      setItems((prev) => [...prev, res.data]);
      if (parent_id) setExpanded((e) => ({ ...e, [parent_id]: true }));
      if (type === "page") {
        setActiveId(res.data._id);
        setDraft({ title: res.data.title, tags: [], content: "" });
        setDirty(false);
      } else {
        setExpanded((e) => ({ ...e, [res.data._id]: true }));
      }
    } catch (e) {
      toast.error("Create failed");
    }
  }

  return (
    <div className="flex h-[calc(100vh-64px-49px)]">
      <aside className="w-72 border-r border-gray-200 bg-white overflow-y-auto">
        <div className="p-4">
          <div className="mb-3">
            <span className="text-xs font-extrabold uppercase tracking-widest text-gray-500">Brain</span>
          </div>

          {items === null && (
            <div className="py-8 flex justify-center">
              <Loader />
            </div>
          )}

          {items !== null && (
            <Tree
              items={items}
              activeId={activeId}
              expanded={expanded}
              onToggle={(id) => setExpanded((e) => ({ ...e, [id]: !e[id] }))}
              onSelect={selectItem}
              onAdd={create}
            />
          )}
        </div>
      </aside>

      <div className="flex-1 overflow-y-auto">
        {items === null && (
          <div className="flex justify-center items-center h-full">
            <Loader />
          </div>
        )}

        {items !== null && !activeItem && <EmptyState onImportClick={() => navigate("/recruiter/brain/onboard")} />}

        {activeItem && activeItem.type === "folder" && (
          <FolderView item={activeItem} onAddPage={() => create("page", activeItem._id)} onAddFolder={() => create("folder", activeItem._id)} onDelete={remove} />
        )}

        {activeItem && activeItem.type === "page" && (
          <Editor
            item={activeItem}
            draft={draft}
            dirty={dirty}
            saving={saving}
            onChange={updateDraft}
            onSave={save}
            onDelete={remove}
          />
        )}
      </div>
    </div>
  );
}

function arraysEq(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function IconButton({ children, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors">
      {children}
    </button>
  );
}

function Tree({ items, activeId, expanded, onToggle, onSelect, onAdd }) {
  const byParent = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const key = it.parent_id || "root";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(it);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        // System pages first at any level
        if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
        // Folders before pages
        if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
        if ((a.order || 0) !== (b.order || 0)) return (a.order || 0) - (b.order || 0);
        return (a.title || "").localeCompare(b.title || "");
      });
    }
    return m;
  }, [items]);

  return (
    <ul className="space-y-0.5">
      <Nodes parent="root" byParent={byParent} depth={0} activeId={activeId} expanded={expanded} onToggle={onToggle} onSelect={onSelect} onAdd={onAdd} />
    </ul>
  );
}

function Nodes({ parent, byParent, depth, activeId, expanded, onToggle, onSelect, onAdd }) {
  const list = byParent.get(parent) || [];
  return list.map((it) => (
    <li key={it._id}>
      <Row
        item={it}
        depth={depth}
        isActive={activeId === it._id}
        isOpen={!!expanded[it._id]}
        hasChildren={(byParent.get(String(it._id)) || []).length > 0}
        onToggle={() => onToggle(it._id)}
        onSelect={() => onSelect(it)}
        onAddPage={() => onAdd("page", it._id)}
        onAddFolder={() => onAdd("folder", it._id)}
      />
      {it.type === "folder" && expanded[it._id] && (
        <ul className="space-y-0.5">
          <Nodes
            parent={String(it._id)}
            byParent={byParent}
            depth={depth + 1}
            activeId={activeId}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAdd={onAdd}
          />
        </ul>
      )}
    </li>
  ));
}

function Row({ item, depth, isActive, isOpen, hasChildren, onToggle, onSelect, onAddPage, onAddFolder }) {
  const isFolder = item.type === "folder";
  return (
    <div
      className={`group flex items-center gap-1.5 pr-1 py-1.5 rounded-lg text-sm transition-colors ${
        isActive ? "bg-primary/10 text-primary font-semibold" : "text-gray-700 hover:bg-gray-50"
      }`}
      style={{ paddingLeft: 8 + depth * 12 }}>
      {isFolder ? (
        <button onClick={onToggle} className="p-0.5 -ml-0.5 rounded hover:bg-black/5">
          {isOpen ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
        </button>
      ) : (
        <span className="w-3.5" />
      )}

      <button onClick={onSelect} className="flex items-center gap-2 flex-1 min-w-0 text-left">
        {isFolder ? <FiFolder className="w-4 h-4 shrink-0" /> : <FiFileText className="w-4 h-4 shrink-0" />}
        <span className="truncate">{item.title || (isFolder ? "Untitled folder" : "Untitled")}</span>
      </button>

      {isFolder && (
        <div className="hidden group-hover:flex items-center gap-0.5">
          <IconButton title="New page" onClick={onAddPage}>
            <FiFilePlus className="w-3.5 h-3.5" />
          </IconButton>
          <IconButton title="New folder" onClick={onAddFolder}>
            <FiFolderPlus className="w-3.5 h-3.5" />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function Editor({ item, draft, dirty, saving, onChange, onSave, onDelete }) {
  const meta = item.is_system ? getSelfPage(item.slug) : null;
  const placeholder = meta?.placeholder || "Write markdown here…";
  const [tagInput, setTagInput] = useState("");
  const tagInputRef = useRef(null);

  function addTag(raw) {
    const t = (raw || "").trim().replace(/^#/, "");
    if (!t) return;
    if (draft.tags.includes(t)) return;
    onChange({ tags: [...draft.tags, t] });
    setTagInput("");
  }

  function removeTag(t) {
    onChange({ tags: draft.tags.filter((x) => x !== t) });
  }

  return (
    <div className="max-w-3xl mx-auto p-8">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          {item.is_system ? (
            <h1 className="text-2xl font-extrabold text-gray-900">{item.title}</h1>
          ) : (
            <input
              type="text"
              value={draft.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="Untitled"
              className="text-2xl font-extrabold text-gray-900 bg-transparent w-full focus:outline-none"
            />
          )}
          {meta?.description && <p className="text-sm text-gray-500 mt-1">{meta.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!item.is_system && (
            <button
              onClick={onDelete}
              className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete">
              <FiTrash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onSave} disabled={!dirty || saving} className="btn btn-primary btn-small">
            <FiSave className="w-4 h-4" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {draft.tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">
            #{t}
            <button onClick={() => removeTag(t)} className="text-gray-400 hover:text-gray-700">
              <FiX className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={tagInputRef}
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addTag(tagInput);
            } else if (e.key === "Backspace" && !tagInput && draft.tags.length) {
              removeTag(draft.tags[draft.tags.length - 1]);
            }
          }}
          onBlur={() => addTag(tagInput)}
          placeholder={draft.tags.length ? "Add tag…" : "Add tags (press Enter)"}
          className="text-xs bg-transparent focus:outline-none py-0.5 px-1 min-w-[120px] flex-1"
        />
      </div>

      <textarea
        value={draft.content}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder={placeholder}
        className="w-full min-h-[60vh] border-2 border-black rounded-xl p-5 font-mono text-sm leading-relaxed bg-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] focus:outline-none focus:ring-2 focus:ring-primary"
      />

      <p className="mt-3 text-xs text-gray-400">
        Markdown. Saved as part of your org's brain — consumed by Claude skills via the MCP.
      </p>
    </div>
  );
}

function FolderView({ item, onAddPage, onAddFolder, onDelete }) {
  return (
    <div className="max-w-3xl mx-auto p-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <FiFolder className="w-5 h-5 text-gray-700" />
          <h1 className="text-2xl font-extrabold text-gray-900">{item.title || "Untitled folder"}</h1>
        </div>
        <button
          onClick={onDelete}
          className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Delete folder">
          <FiTrash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="border-2 border-black rounded-xl p-6 bg-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] flex items-center gap-3">
        <button onClick={onAddPage} className="btn btn-primary btn-small">
          <FiFilePlus className="w-4 h-4" />
          New page
        </button>
        <button onClick={onAddFolder} className="btn btn-secondary btn-small">
          <FiFolderPlus className="w-4 h-4" />
          New folder
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onImportClick }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8">
      <div className="w-16 h-16 rounded-2xl border-2 border-black bg-white flex items-center justify-center mb-6 shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
        <FiCpu className="w-7 h-7 text-gray-700" />
      </div>
      <h2 className="text-xl font-extrabold text-gray-900 mb-2">Pick a page or generate from your website</h2>
      <p className="text-sm text-gray-500 max-w-md mb-6">
        Click a SELF page on the left to start writing, or generate the 5 SELF pages from your website in one shot.
      </p>
      <button onClick={onImportClick} className="btn btn-primary">
        Generate from website
      </button>
    </div>
  );
}
